import React, { useCallback, useEffect, useState } from "react";

import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { Shimmer } from "../components/MotionPrimitives";
import ResellerManagedBadge from "../components/ResellerManagedBadge";
import apiService from "../services/api";
import {
  logError,
  sanitizeInput,
  getErrorMessage,
  validateRequired,
} from "../utils/errorHandler";

const Customers = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Initialize typeFilter from URL parameter
  const initialTypeFilter = searchParams.get("type") || null;

  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc"); // 'asc' or 'desc'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Number of items per page
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0,
  });
  const [stats, setStats] = useState({
    total: 0,
    freeUsers: 0,
    premium: 0,
    trial: 0,
    resellers: 0,
  });
  const [typeFilter, setTypeFilter] = useState(initialTypeFilter); // null = all, or "Free User", "Premium", "Trial", "Reseller"
  const [statusFilter, setStatusFilter] = useState(""); // "" = all, "ACTIVE", or "INACTIVE"
  const [subscriptionFilter, setSubscriptionFilter] = useState(""); // "" = all, "N/A", "single-use", "monthly", "quarterly", "yearly"
  const [dateFrom, setDateFrom] = useState(""); // Start date for filtering
  const [dateTo, setDateTo] = useState(""); // End date for filtering
  const [editingCustomer, setEditingCustomer] = useState(null);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      // Map frontend sort field to API sort field
      const sortFieldMap = {
        createdAt: "created_at",
        name: "email_address",
        totalSpent: "total_spent",
        payments: "payments",
        tickets: "tickets",
        type: "type",
        status: "status",
        subscription: "subscription",
      };

      const apiSortField = sortFieldMap[sortField] || "created_at";
      const apiSortOrder = sortDirection.toUpperCase();

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sort: apiSortField,
        order: apiSortOrder,
      };

      // Add search param if present
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }

      // Add type filter if present
      if (typeFilter) {
        params.type = typeFilter;
      }

      // Add status filter if present
      if (statusFilter) {
        params.status = statusFilter;
      }

      // Add subscription filter if present
      if (subscriptionFilter) {
        params.subscription = subscriptionFilter;
      }

      // Add date filters if present
      if (dateFrom) {
        params.dateFrom = dateFrom;
      }
      if (dateTo) {
        params.dateTo = dateTo;
      }

      const response = await apiService.getCustomers(params);
      // The API now returns { status: 200, data: { stats, customers }, pagination: {...} }
      const responseData = response.data || {};
      const customers = responseData.customers || [];
      const statsData = responseData.stats || {
        total: 0,
        freeUsers: 0,
        premium: 0,
        trial: 0,
        resellers: 0,
      };
      const paginationData = response.pagination || {
        total: 0,
        page: 1,
        limit: itemsPerPage,
        pages: 0,
      };

      setCustomers(Array.isArray(customers) ? customers : []);
      setStats(statsData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Error fetching customers:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to load customers";

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    dateFrom,
    dateTo,
    debouncedSearchTerm,
    itemsPerPage,
    sortDirection,
    sortField,
    statusFilter,
    subscriptionFilter,
    typeFilter,
  ]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      setDebouncedSearchTerm(searchTerm.trim());
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Handle sorting - all fields now supported via API
  const handleSort = (field) => {
    // All sortable fields are now supported by the API
    const supportedFields = [
      "name",
      "createdAt",
      "type",
      "totalSpent",
      "payments",
      "tickets",
      "status",
      "subscription",
    ];

    if (!supportedFields.includes(field)) {
      toast.info("Sorting by this field is not yet supported");

      return;
    }

    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Check if advanced filters panel should show active state
  const hasActiveFilters =
    dateFrom || dateTo || statusFilter || subscriptionFilter;

  // Display customers directly from API (already sorted and paginated)
  const displayCustomers = customers;
  const currentItems = displayCustomers;
  const totalPages = pagination.pages;
  const indexOfFirstItem = (pagination.page - 1) * pagination.limit;
  const indexOfLastItem = Math.min(
    indexOfFirstItem + displayCustomers.length,
    pagination.total
  );

  // Handle page changes
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate page numbers for pagination UI
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages === 0) return pages;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (pagination.page <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (pagination.page >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = pagination.page - 1; i <= pagination.page + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "N/A";

    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Handle clicking on a stats card to filter
  const handleStatsCardClick = (filterType) => {
    // If clicking the same filter, toggle it off (show all)
    // If clicking a different filter, apply it
    setTypeFilter(typeFilter === filterType ? null : filterType);
    // Reset to page 1 when filter changes
    setCurrentPage(1);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setStatusFilter("");
    setSubscriptionFilter("");
    setCurrentPage(1);
  };

  // Stats cards using API-provided global stats
  const statsCards = [
    {
      label: "Total Customers",
      value: stats.total,
      icon: "fas fa-users",
      color: "bg-blue-500",
      filterType: null, // null means "all customers"
    },
    {
      label: "Free Users",
      value: stats.freeUsers,
      icon: "fas fa-user",
      color: "bg-gray-500",
      filterType: "Free User",
    },
    {
      label: "Premium Users",
      value: stats.premium,
      icon: "fas fa-crown",
      color: "bg-purple-500",
      filterType: "Premium",
    },
    {
      label: "Trial Users",
      value: stats.trial,
      icon: "fas fa-clock",
      color: "bg-blue-400",
      filterType: "Trial",
    },
    {
      label: "Resellers",
      value: stats.resellers,
      icon: "fas fa-handshake",
      color: "bg-orange-500",
      filterType: "Reseller",
    },
  ];

  return (
    <div className="w-full space-y-4 sm:space-y-6">
      <div className="w-full space-y-4 sm:space-y-6">
        {/* Stats */}
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {statsCards.map((stat, index) => {
            const isActive = typeFilter === stat.filterType;

            return (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white dark:bg-gray-800/50 rounded-xl shadow-md border cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
                  isActive
                    ? "border-primary-500 dark:border-primary-400 ring-2 ring-primary-200 dark:ring-primary-800"
                    : "border-gray-100 dark:border-gray-700/50 hover:border-primary-300 dark:hover:border-primary-600"
                }`}
                initial={{ opacity: 0, y: 20 }}
                key={index}
                style={{
                  padding: "16px",
                  overflow: "visible",
                }}
                transition={{ delay: index * 0.1 }}
                onClick={() => handleStatsCardClick(stat.filterType)}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%",
                    minHeight: "40px",
                  }}
                >
                  <div
                    className={`${stat.color} rounded-lg`}
                    style={{
                      width: "40px",
                      height: "40px",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i
                      className={`${stat.icon} text-white`}
                      style={{
                        fontSize: "16px",
                        display: "block",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      className="font-bold text-gray-900 dark:text-white"
                      style={{
                        fontSize: "20px",
                        lineHeight: "1.2",
                        display: "block",
                        visibility: "visible",
                        opacity: 1,
                        color: "inherit",
                      }}
                    >
                      {stat.value}
                    </div>
                    <div
                      className="text-gray-600 dark:text-gray-300"
                      style={{
                        fontSize: "12px",
                        marginTop: "2px",
                        display: "block",
                        visibility: "visible",
                        opacity: 1,
                        color: "inherit",
                      }}
                    >
                      {stat.label}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Filters and Search */}
        <div className="card p-6 shadow-md w-full">
          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
            <div className="relative flex-1 w-full">
              <input
                className="input pl-10 w-full"
                placeholder="Search by name, email, or company..."
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              {searchTerm && (
                <button
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  onClick={() => setSearchTerm("")}
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
            <select
              className="input w-full sm:w-auto"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
        </div>

        {/* Advanced Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md w-full">
          <button
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            onClick={() => setShowFilters(!showFilters)}
          >
            <div className="flex items-center gap-2">
              <i className="fas fa-filter text-gray-500 dark:text-gray-400" />
              <span className="font-medium text-gray-900 dark:text-white">
                Advanced Filters
              </span>
              {hasActiveFilters && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-full">
                  Active
                </span>
              )}
            </div>
            <i
              className={`fas fa-chevron-${showFilters ? "up" : "down"} text-gray-400`}
            />
          </button>

          {showFilters && (
            <div className="px-6 pb-6 pt-2 border-t border-gray-200 dark:border-gray-700">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sign Up Date From
                    </label>
                    <input
                      className="input w-full"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => {
                        setDateFrom(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sign Up Date To
                    </label>
                    <input
                      className="input w-full"
                      type="date"
                      value={dateTo}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <select
                      className="input w-full"
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">All Statuses</option>
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                      <option value="SUSPENDED">Suspended</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Subscription
                    </label>
                    <select
                      className="input w-full"
                      value={subscriptionFilter}
                      onChange={(e) => {
                        setSubscriptionFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">All Subscriptions</option>
                      <option value="N/A">N/A</option>
                      <option value="single-use">Single-use</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                </div>
                {hasActiveFilters && (
                  <div className="flex justify-end">
                    <button
                      className="btn btn-ghost text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={handleClearFilters}
                    >
                      <i className="fas fa-times mr-2" />
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Customers Table */}
        <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-md border border-gray-100 dark:border-gray-700/50 w-full">
          <div
            className="overflow-x-auto w-full"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    #
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      {sortField === "name" && (
                        <i
                          className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                        />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort("email")}
                  >
                    <div className="flex items-center gap-1">
                      Email
                      {sortField === "email" && (
                        <i
                          className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                        />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort("subscription")}
                  >
                    <div className="flex items-center gap-1">
                      Subscription
                      {sortField === "subscription" && (
                        <i
                          className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                        />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort("type")}
                  >
                    <div className="flex items-center gap-1">
                      Type
                      {sortField === "type" && (
                        <i
                          className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                        />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort("createdAt")}
                  >
                    <div className="flex items-center gap-1">
                      Signed Up
                      {sortField === "createdAt" && (
                        <i
                          className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                        />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort("totalSpent")}
                  >
                    <div className="flex items-center gap-1">
                      Total Spent
                      {sortField === "totalSpent" && (
                        <i
                          className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                        />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort("payments")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Payments
                      {sortField === "payments" && (
                        <i
                          className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                        />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort("tickets")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Tickets
                      {sortField === "tickets" && (
                        <i
                          className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                        />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center gap-1">
                      Status
                      {sortField === "status" && (
                        <i
                          className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                        />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  Array.from({ length: 8 }).map((_, idx) => (
                    <tr key={idx}>
                      {Array.from({ length: 10 }).map((__, cellIdx) => (
                        <td className="px-3 py-3" key={cellIdx}>
                          <Shimmer className="h-3.5 w-full rounded-md" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : displayCustomers.length === 0 ? (
                  <tr>
                    <td
                      className="text-center py-12 text-gray-500"
                      colSpan="10"
                    >
                      No customers found
                    </td>
                  </tr>
                ) : (
                  currentItems.map((customer, index) => (
                    <tr
                      className={`cursor-pointer ${
                        index % 2 === 0
                          ? "table-row-striped-even"
                          : "table-row-striped-odd"
                      } table-row-hover`}
                      key={customer.id}
                      onClick={() => navigate(`/customers/${customer.id}`)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {indexOfFirstItem + index + 1}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {customer.avatarUrl ? (
                            <img
                              alt={customer.name}
                              className="w-8 h-8 rounded-full object-cover"
                              src={customer.avatarUrl}
                            />
                          ) : (
                            <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                              {customer.name?.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div
                            className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[150px]"
                            title={customer.name}
                          >
                            {customer.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div
                          className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-[200px]"
                          title={customer.email}
                        >
                          {customer.email}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {customer.subscription || customer.type || "N/A"}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              customer.type === "Reseller"
                                ? "bg-[#e97b35] text-white"
                                : customer.type === "Premium"
                                  ? "bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 dark:from-purple-900 dark:to-purple-800 dark:text-purple-200"
                                  : customer.type === "Trial"
                                    ? "bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 dark:from-blue-900 dark:to-blue-800 dark:text-blue-200"
                                    : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                            }`}
                          >
                            {customer.type || "Free User"}
                          </span>
                          {customer.resellerManaged && (
                            <ResellerManagedBadge size="sm" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(
                            customer.createdAt || customer.created_at
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          $
                          {(customer.totalSpent || 0).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {customer.payments || 0}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                          {customer.tickets || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            customer.status?.toUpperCase() === "ACTIVE"
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                              : customer.status?.toUpperCase() === "INACTIVE"
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                                : customer.status?.toUpperCase() === "SUSPENDED"
                                  ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                  : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                          }`}
                        >
                          {customer.status
                            ? customer.status.charAt(0).toUpperCase() +
                              customer.status.slice(1).toLowerCase()
                            : "Active"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="w-full p-4 border-t border-gray-200 dark:border-gray-700 flex flex-col lg:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center lg:text-left">
                Showing {indexOfFirstItem + 1} to {indexOfLastItem} of{" "}
                {pagination.total} customers
              </p>
              <div className="flex gap-2 flex-wrap justify-center">
                <button
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-md hover:scale-105"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Previous
                </button>
                {getPageNumbers().map((page, index) =>
                  page === "..." ? (
                    <span
                      className="px-2 py-1 text-sm text-gray-500"
                      key={`ellipsis-${index}`}
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      className={`px-3 py-1 text-sm border rounded-lg transition-all duration-200 hover:shadow-md hover:scale-105 ${
                        currentPage === page
                          ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                          : "border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                      key={page}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  )
                )}
                <button
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-md hover:scale-105"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Customer Modal */}
      <AnimatePresence>
        {showEditModal && editingCustomer && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md"
              exit={{ scale: 0.9, opacity: 0 }}
              initial={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Edit Customer
              </h2>
              <form
                className="space-y-4"
                onSubmit={async (e) => {
                  e.preventDefault();

                  // Validate required fields
                  const validation = validateRequired(editingCustomer, [
                    "name",
                    "email",
                  ]);

                  if (!validation.isValid) {
                    toast.error(validation.errors[0]);

                    return;
                  }

                  try {
                    // Sanitize and prepare update data
                    const sanitized = sanitizeInput(editingCustomer);
                    const updateData = {
                      firstName: sanitized.name?.split(" ")[0] || "",
                      lastName:
                        sanitized.name?.split(" ").slice(1).join(" ") || "",
                      email: sanitized.email,
                      phone: sanitized.phone || "",
                      company: sanitized.company || "",
                      location: sanitized.location || "",
                      timezone: sanitized.timezone || "",
                      bio: sanitized.bio || "",
                      website: sanitized.website || "",
                      position: sanitized.position || "",
                    };

                    const response = await apiService.updateCustomer(
                      editingCustomer.id,
                      updateData
                    );

                    if (response?.status === 200 || response?.data) {
                      toast.success("Customer updated successfully");
                      setShowEditModal(false);
                      setEditingCustomer(null);
                      await fetchCustomers(); // Refresh the list
                    } else {
                      throw new Error(response?.error || "Update failed");
                    }
                  } catch (error) {
                    logError("CustomerUpdate", error, {
                      customerId: editingCustomer.id,
                    });
                    toast.error(
                      getErrorMessage(error, "Failed to update customer")
                    );
                  }
                }}
              >
                <div>
                  <label className="label">Full Name</label>
                  <input
                    required
                    className="input"
                    type="text"
                    value={editingCustomer.name}
                    onChange={(e) =>
                      setEditingCustomer({
                        ...editingCustomer,
                        name: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input
                    required
                    className="input"
                    type="email"
                    value={editingCustomer.email}
                    onChange={(e) =>
                      setEditingCustomer({
                        ...editingCustomer,
                        email: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input
                    className="input"
                    type="tel"
                    value={editingCustomer.phone || ""}
                    onChange={(e) =>
                      setEditingCustomer({
                        ...editingCustomer,
                        phone: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="label">Company</label>
                  <input
                    className="input"
                    type="text"
                    value={editingCustomer.company || ""}
                    onChange={(e) =>
                      setEditingCustomer({
                        ...editingCustomer,
                        company: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="label">Status</label>
                  <select
                    className="input"
                    value={editingCustomer.status || "active"}
                    onChange={(e) =>
                      setEditingCustomer({
                        ...editingCustomer,
                        status: e.target.value,
                      })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    className="btn btn-ghost flex-1 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 hover:scale-105"
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingCustomer(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary flex-1 hover:bg-primary-700 hover:shadow-lg transition-all duration-200 hover:scale-105"
                    type="submit"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Customers;
